/**
 * Shared read + fetch auth helper. The Connect shell owns login / refresh /
 * token-writes (`connect/src/auth.ts`); this module is the read side every
 * sub-app talks to. It reads the shell-written bearer out of localStorage
 * (`blp_token`, mirrored into each `<app>_token`) and, on a 401, asks the shell
 * to refresh via the `window.formica.tryRefresh()` bridge before retrying once.
 */

import { localeHeader } from '../http/locale.js';
import { fetchWithBackoff, type BackoffOptions } from '../http/retry.js';

// `nx` mirrors into `nx_token`, which erp reads directly. The shell writes
// `blp_token` plus the per-app keys in this list, so the fallback keeps working
// even if a caller only ever saw the legacy slot.
const SUBAPPS = [
  'forge',
  'agent',
  'flow',
  'chat',
  'mail',
  'calendar',
  'calc',
  'drive',
  'nx',
  'designer',
  'wiki',
  'sites',
  'write',
] as const;

const API_BASE_STORAGE_KEY = 'formica.api.baseUrl';

/** Default API origin when nothing is configured: the page host on :7680
 *  (so a non-localhost deployment hits its own host), localhost off-DOM. */
function defaultApiBase(): string {
  const host = typeof window !== 'undefined' ? window.location?.hostname : '';
  return `http://${host || 'localhost'}:7680`;
}

function viteEnvValue(key: string): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return env?.[key] || '';
}

function safeLocalStorageGet(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function stripV1(url: string): string {
  return url.replace(/\/v1\/?$/, '').replace(/\/$/, '');
}

/**
 * Returns the API origin without `/v1` suffix (e.g. `http://host:port`).
 * Mirrors the shell's resolution order: `?url=` query param, then the stored
 * `formica.api.baseUrl`, then `window.__API_URL__`, then `VITE_API_URL`.
 */
export function getApiOrigin(): string {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const urlParam = params.get('url');
    if (urlParam) {
      try {
        return stripV1(atob(urlParam));
      } catch {
        /* fall through */
      }
    }
  }
  const stored = safeLocalStorageGet(API_BASE_STORAGE_KEY);
  if (stored) return stripV1(stored);
  return getDeploymentApiOrigin();
}

/**
 * The API origin of the deployment this page was served for: the origin the
 * host injected (`window.__API_URL__`), else the build's `VITE_API_URL`, else
 * the page's own host on :7680 — what {@link getApiOrigin} falls back to when
 * nothing is saved, and none of the overrides (`?url=`, the saved
 * `formica.api.baseUrl`, which an earlier sign-in elsewhere leaves behind).
 * For credentials that belong to this deployment only, such as a mailed
 * sign-in link.
 */
export function getDeploymentApiOrigin(): string {
  const injected =
    typeof window !== 'undefined'
      ? (window as Window & { __API_URL__?: string }).__API_URL__
      : undefined;
  if (injected) return stripV1(injected);
  return stripV1(viteEnvValue('VITE_API_URL') || defaultApiBase());
}

export function readBearer(): string {
  const token = safeLocalStorageGet('blp_token');
  if (token) return token;
  for (const app of SUBAPPS) {
    const legacy = safeLocalStorageGet(`${app}_token`);
    if (legacy) return legacy;
  }
  return '';
}

/**
 * True when the shell has stored a bearer. The OAuth access token is the only
 * credential the backend accepts; the session UUID never authenticates.
 */
export function hasAuthCredentials(): boolean {
  return readBearer().length > 0;
}

/**
 * Merges the current shell bearer into the given headers as `Authorization`
 * and the shell's UI language as `Accept-Language` (read now, so a language
 * switch applies to the next request) — the language the backend answers its
 * error messages in. An `Accept-Language` the caller set stays as it is.
 */
export function buildAuthHeaders(init: HeadersInit = {}): Headers {
  const headers = new Headers(init);
  const bearer = readBearer();
  if (bearer) headers.set('Authorization', `Bearer ${bearer}`);
  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', localeHeader()['Accept-Language']);
  }
  return headers;
}

/**
 * Asks the Connect shell to refresh the access token. The shell installs
 * `window.formica.tryRefresh` (`connect/src/auth.ts`); when it is absent (e.g.
 * a sub-app loaded without the shell) we report failure so the caller surfaces
 * the original 401. Exported so non-fetch clients (e.g. erp's axios layer) can
 * run the same single-in-flight refresh before retrying a 401.
 */
export async function tryShellRefresh(): Promise<boolean> {
  const bridge = (globalThis as { formica?: { tryRefresh?: () => Promise<boolean> } }).formica;
  if (!bridge?.tryRefresh) return false;
  try {
    return await bridge.tryRefresh();
  } catch {
    return false;
  }
}

/** The method a fetch of input with init sends (undefined: fetch's GET). */
function requestMethod(input: RequestInfo | URL, init: RequestInit): string | undefined {
  if (init.method) return init.method;
  return typeof Request !== 'undefined' && input instanceof Request ? input.method : undefined;
}

/**
 * `fetch` with shell auth + transient backoff. Auth headers are rebuilt per
 * attempt (so a refresh mid-flight is picked up), the transient statuses of
 * the request's method (`defaultRetryStatuses`: never a 500; a POST or PATCH
 * only when it was not processed) and network errors back off, and a single
 * 401 triggers one `window.formica.tryRefresh()` followed by exactly one
 * retry with the fresh bearer.
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init: RequestInit = {},
  backoff?: BackoffOptions
): Promise<Response> {
  // The login this request starts under: every attempt — a backoff retry as well
  // as the retry after a refresh — rebuilds its bearer from storage, so a login
  // that replaced this one meanwhile (another account, a same-tab switch) would
  // carry the request with ITS bearer. Such an attempt is never made.
  const login = readLoginMarker();
  const sameLogin = () => !login || readLoginMarker() === login;
  const doFetch = () => {
    if (!sameLogin()) throw new Error('login_changed');
    return fetch(input, { ...init, headers: buildAuthHeaders(init.headers) });
  };
  backoff = { method: requestMethod(input, init), ...backoff };

  const initial = await fetchWithBackoff(doFetch, backoff);
  if (initial.status !== 401) return initial;

  const refreshed = await tryShellRefresh();
  if (!sameLogin()) throw new Error('login_changed');
  if (!refreshed) return initial;

  return fetchWithBackoff(doFetch, backoff);
}

const SHARED_OAUTH_SESSION_KEY = 'formica.oauth.session';

/** The window event the shell fires synchronously before it replaces the session and reloads: session-bound resources (the mux) close on it. */
export const SESSION_ENDING_EVENT = 'formica:session-ending';

/** The marker of the current login (`formica.oauth.loginId`, kept by the shell next to the credentials); null when there is none. */
function readLoginMarker(): string | null {
  return safeLocalStorageGet('formica.oauth.loginId');
}

/**
 * The window event the shell fires after the user changed the API base (the
 * server field on the login page). The shared mux socket closes a connection
 * to the old origin on it, so nothing keeps talking to the previous server.
 */
export const API_BASE_CHANGED_EVENT = 'formica:api-base-changed';

/**
 * The API origin (without `/v1`) that issued the stored bearer, read from the
 * shell-written session blob; '' when unknown. Credentials are only ever sent
 * to this origin.
 */
export function readBearerOrigin(): string {
  const raw = safeLocalStorageGet(SHARED_OAUTH_SESSION_KEY);
  if (!raw) return '';
  try {
    const value = (JSON.parse(raw) as { apiBaseUrl?: unknown }).apiBaseUrl;
    return typeof value === 'string' && value ? stripV1(value) : '';
  } catch {
    return '';
  }
}

/** Shell-provided principal (account or user). `name` may be empty. */
export interface ShellPrincipal {
  email: string;
  name: string;
  /** Stable account uuid when the persisted shell session carries one (chat
   *  uses it to resolve its own account id); undefined for sessions that only
   *  expose email/name. */
  uuid?: string;
}

function readSharedSessionPrincipal(field: 'account' | 'user'): ShellPrincipal | null {
  const raw = safeLocalStorageGet(SHARED_OAUTH_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const value = parsed[field] as { email?: unknown; name?: unknown; uuid?: unknown } | undefined;
    const email = typeof value?.email === 'string' ? value.email : '';
    if (!email) return null;
    return {
      email,
      name: typeof value?.name === 'string' ? value.name : '',
      uuid: typeof value?.uuid === 'string' ? value.uuid : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * The logged-in account / user, read from the shell-written session blob.
 * Sub-apps use this for identity (e.g. the JMAP `accountId` email) without
 * touching the storage key directly. Returns null when logged out.
 */
export function readShellAccount(): ShellPrincipal | null {
  return readSharedSessionPrincipal('account');
}

export function readShellUser(): ShellPrincipal | null {
  return readSharedSessionPrincipal('user');
}

/**
 * The JMAP `accountId` of the logged-in account: its stable uuid, which is
 * the key the mail and calendar session documents list under `accounts`.
 *
 * Never the email. A JMAP host compares the `accountId` of a method call
 * against the account it authenticated and answers `accountNotFound` on a
 * mismatch, and an address change would silently invalidate every stored
 * id. The email is a fallback only for a shell session old enough to have
 * been written without a uuid.
 */
export function readShellAccountId(): string {
  const account = readShellAccount();
  const user = readShellUser();
  return account?.uuid || user?.uuid || account?.email || user?.email || '';
}

/**
 * Asks the Connect shell to sign out: it ends the session server-side (every
 * session of the account when `allDevices`), clears credentials and redirects
 * to login. The shell installs
 * `window.formica.signOut`; a no-op when the bridge is absent.
 */
export function signOutFromShell(allDevices = false): void | Promise<void> {
  const bridge = (
    globalThis as {
      formica?: { signOut?: (allDevices?: boolean) => void | Promise<void> };
    }
  ).formica;
  return bridge?.signOut?.(allDevices);
}

/**
 * An impersonation session handed to the shell (I-2327): what master's
 * `POST /v1/accounts/:id/impersonate` answered, plus the identity sub-apps
 * read and the shell route to return to afterwards.
 */
export interface ShellImpersonationStart {
  accessToken: string;
  /** RFC 3339 end of the session. */
  expiresAt?: string;
  accountID?: number;
  account?: { id?: number; email?: string; name?: string; uuid?: string } | null;
  /** Shell route path (`route.fullPath`), never a URL. */
  returnTo?: string;
}

/**
 * Asks the Connect shell to start an impersonation session: it keeps the
 * current credentials aside, runs every tab on the impersonation token and
 * reloads into the shell home. The token only ever travels through this call,
 * never a URL. Throws `impersonation_unavailable` without the shell, and
 * passes on the shell's own refusal.
 */
export async function startShellImpersonation(start: ShellImpersonationStart): Promise<void> {
  const bridge = (
    globalThis as {
      formica?: { startImpersonation?: (start: ShellImpersonationStart) => Promise<void> };
    }
  ).formica;
  if (!bridge?.startImpersonation) throw new Error('impersonation_unavailable');
  await bridge.startImpersonation(start);
}

/** Switch through the shell's guard/refresh/reset flow, then open a local route. */
export async function switchShellScope(
  tenantID: number,
  companyID: number,
  destination: string
): Promise<void> {
  const bridge = (
    globalThis as {
      formica?: {
        switchScope?: (tenantID: number, companyID: number, destination: string) => Promise<void>;
      };
    }
  ).formica;
  if (!bridge?.switchScope) throw new Error('scope_switch_unavailable');
  await bridge.switchScope(tenantID, companyID, destination);
}

/** The tenant and company the shell's session works in (master ids). */
export interface ShellScope {
  tenantID: number;
  companyID: number;
}

/**
 * Where the shell's session works right now. An app that stores a link to a
 * company-bound record (an ERP record on a mail, a channel in a chat link)
 * notes the company with it, so the link stays unambiguous for a reader who
 * works in another company. null without the shell or while it does not know.
 */
export async function readShellScope(): Promise<ShellScope | null> {
  const bridge = (globalThis as { formica?: { sessionScope?: () => Promise<ShellScope | null> } })
    .formica;
  if (!bridge?.sessionScope) return null;
  try {
    return await bridge.sessionScope();
  } catch {
    return null;
  }
}

/**
 * Opens a local route in a company of the session, named by its master id —
 * the one way an app follows a link to a company-bound record. Where the
 * session already works in that company this is a plain navigation; otherwise
 * the shell switches the session through its guard/refresh/reset flow and
 * reloads every tab of the session, this one into the route.
 *
 * `destination` is a route path (`router.resolve(...).fullPath`), never a
 * URL. Throws `scope_switch_unavailable` without the shell and passes on the
 * shell's refusal: `company_not_granted` for a company the account may not
 * work in, master's other switch codes, `invalid_scope_destination`.
 */
export async function openInShellCompany(companyID: number, destination: string): Promise<void> {
  const bridge = (
    globalThis as {
      formica?: { openInCompany?: (companyID: number, destination: string) => Promise<void> };
    }
  ).formica;
  if (!bridge?.openInCompany) throw new Error('scope_switch_unavailable');
  await bridge.openInCompany(companyID, destination);
}
